import React, {
    useState,
    useMemo,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@appkit4/react-components/button';
import { Search } from '@appkit4/react-components/search';
import { useQueryClient } from '@tanstack/react-query';
import {
    type ColumnDef,
    type ColumnFiltersState,
    type RowSelectionState,
    type SortingState,
} from '@tanstack/react-table';
import MenuButton from '../../components/reusables/MenuButton';
import Tabs, { TabConfig } from '../../components/reusables/Tabs';
import Icon from '../../components/reusables/Icon';
import IconButton from '../../components/reusables/IconButton';
import Pagination from '../../components/reusables/Pagination';
import DataTable from '../../components/reusables/DataTable';
import ResolvePerAssetModal from '../fixedasset/ResolvePerAssetModal';
import ResolvePerRuleModal from '../fixedasset/ResolvePerRuleModal';
import KeywordAnalysisModal, {
    Keyword,
} from '../fixedasset/KeywordAnalysisModal';
import AddRuleModal, { SplitRuleValues } from '../fixedasset/AddRuleModal';
import ManualCatModal from './ManualCatModal';
import { conflictResolveContext } from '../../types/conflictResolveContext';
import { ReactComponent as DownloadIcon } from '../../assets/icons/New Brand Icons/download.svg';
import { ReactComponent as UploadIcon } from '../../assets/icons/New Brand Icons/upload.svg';
import Modal from '../../components/reusables/Modal';
import ImportDataModal, {
    ImportDataDraft,
} from '../../components/reusables/ImportDataModal';
import { useProject } from '../../context/ProjectContext';
import { useEngagement } from '../../context/EngagementContext';
import { useTenant } from '../../context/TenantContext';
import {
    getGridColumnVisibilityKey,
    readHiddenColumnIds,
    saveHiddenColumnIds,
} from '../../helpers/gridColumnVisibility';
import { DataSourceTypeId } from '../../Constants';
import {
    useDeleteProject,
    useCreateProject,
    useGetDataGrid,
    useGetDatasetColumns,
    getGetProjectsForEntityQueryKey,
    useGetUserInfo,
    useGetPools,
    useApplyManualProjectPoolSplits,
    getGetProjectProgressBarStatusQueryKey,
    useUploadImportFile,
    useCommitProjectDataImport,
    useGetDataFieldDefinitions,
    getGetDataGridQueryKey,
    getGetDatasetColumnsQueryKey,
} from '../../api/generated/endpoint';
import type { ProjectListItemResponse } from '../../api/generated/models';
import useToast from '../../helpers/useToast';
import useGenericErrorToast from '../../helpers/useGenericErrorToast';
import AddProjectModal from './AddProjectModal';
import {
    getImportUploadErrorMessage,
    ImportUploadResponse,
} from '../../api/imports';

const tabs: TabConfig[] = [
    { label: 'Projects', value: 'projects' },
    { label: 'Analysis', value: 'analysis' },
];

const ANALYSIS_PAGE_SIZE = 25;
const DESCRIPTION_COLUMN_MAX_WIDTH = 360;
const PROJECT_DESCRIPTION_COLUMN_WIDTH = 260;
const NUMERIC_COLUMN_WIDTH = 140;

const conflictResolveOptions = [
    { label: 'Resolve Per Asset', value: 'perAsset' },
    { label: 'Resolve Per Rule', value: 'perRule' },
];

const ProjectData: React.FC = () => {
    const queryClient = useQueryClient();
    const { selectedEntityId, activeTimeId } = useEngagement();
    const { selectedTenant } = useTenant();
    const { data: userInfo } = useGetUserInfo();
    const { id: routeTenantId } = useParams();
    const parsedRouteTenantId = Number(routeTenantId);
    const tenantId =
        Number.isInteger(parsedRouteTenantId) && parsedRouteTenantId > 0
            ? parsedRouteTenantId
            : (selectedTenant?.tenantId ?? userInfo?.tenants?.[0]?.tenantId);
    // FASORT did not offer project imports to reviewers; the API rejects them as well.
    const isReviewer =
        userInfo?.tenants
            ?.find((tenant) => tenant.tenantId === tenantId)
            ?.roleKey?.toUpperCase() === 'REVIEWER';
    const { projects, setActiveProjectId, setActiveVersion } = useProject();
    const showToast = useToast();
    const showGenericErrorToast = useGenericErrorToast();

    const { mutate: deleteProjectMutate, isPending: isDeleting } =
        useDeleteProject({
            mutation: {
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: getGetProjectsForEntityQueryKey({
                            entityId: selectedEntityId,
                            timeId: activeTimeId,
                        }),
                    });
                    showToast(
                        'Success',
                        'Project deleted successfully',
                        'success'
                    );
                    handleCloseDeleteProject();
                },
                onError: (error) => {
                    showGenericErrorToast(error);
                },
            },
        });
    const deleteProjectMutation = {
        mutate: deleteProjectMutate,
        isPending: isDeleting,
    };

    const { mutate: createProjectMutate, isPending: isCreating } =
        useCreateProject({
            mutation: {
                onSuccess: () => {
                    queryClient.invalidateQueries({
                        queryKey: getGetProjectsForEntityQueryKey({
                            entityId: selectedEntityId,
                            timeId: activeTimeId,
                        }),
                    });
                    showToast(
                        'Success',
                        'Project created successfully',
                        'success'
                    );
                    setShowAddProject(false);
                },
                onError: (error) => {
                    showGenericErrorToast(error);
                },
            },
        });

    const [showAddProject, setShowAddProject] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>('projects');
    const [projectSearchValue, setProjectSearchValue] = useState<string>('');

    const [analysisSearchValue, setAnalysisSearchValue] = useState<string>('');
    const [analysisPage, setAnalysisPage] = useState<number>(1);
    const [analysisPageSize, setAnalysisPageSize] =
        useState<number>(ANALYSIS_PAGE_SIZE);
    const [hiddenColumnIds, setHiddenColumnIds] = useState<Set<number>>(
        new Set()
    );

    const [selectedProjectId, setSelectedProjectId] = useState<
        number | undefined
    >(undefined);
    const [selectedVersion, setSelectedVersion] = useState<number | undefined>(
        undefined
    );

    const [sortColumnId, setSortColumnId] = useState<number | undefined>(
        undefined
    );
    const [sortDescending, setSortDescending] = useState<boolean>(false);
    const [columnFilters, setColumnFilters] = useState<Record<number, string>>(
        {}
    );

    const isAnalysisScopeReady =
        !!selectedEntityId &&
        !!activeTimeId &&
        selectedProjectId !== undefined &&
        selectedVersion !== undefined;
    const [manualAssignmentModalVisible, setManualAssignmentModalVisible] =
        useState<boolean>(false);
    const [analysisRowSelectionState, setAnalysisRowSelectionState] =
        useState<RowSelectionState>({});
    const selectedAnalysisRowIds = useMemo(
        () =>
            Object.keys(analysisRowSelectionState)
                .filter((rowId) => analysisRowSelectionState[rowId])
                .map(Number),
        [analysisRowSelectionState]
    );

    useEffect(() => {
        if (!projects || projects.length === 0) return;

        const selectedProjectExists = projects.some(
            (project) => project.projectID === selectedProjectId
        );
        if (selectedProjectExists) return;

        const firstProject = projects[0];
        const firstAvailableVersion: number | undefined =
            firstProject.availableVersions?.[0];
        const activeVersion: number | undefined =
            firstProject.activeVersion ?? undefined;
        const initialVersion = firstAvailableVersion ?? activeVersion;
        setSelectedProjectId(firstProject.projectID);
        setSelectedVersion(initialVersion);
        setActiveProjectId(firstProject.projectID);
        setActiveVersion(initialVersion);
    }, [projects, selectedProjectId, setActiveProjectId, setActiveVersion]);

    useEffect(() => {
        setAnalysisRowSelectionState({});
    }, [
        analysisPage,
        analysisSearchValue,
        selectedProjectId,
        selectedVersion,
        sortColumnId,
        sortDescending,
    ]);

    const { data: analysisColumnsData } = useGetDatasetColumns(
        {
            entityId: selectedEntityId!,
            timeId: activeTimeId!,
            dataSourceTypeId: DataSourceTypeId.Project,
            projectId: selectedProjectId,
            version: selectedVersion,
        },
        { query: { enabled: isAnalysisScopeReady } }
    );

    useEffect(() => {
        const storageKey = getGridColumnVisibilityKey({
            userId: userInfo?.id,
            tenantId,
            entityId: selectedEntityId,
            timeId: activeTimeId,
            dataSourceTypeId: DataSourceTypeId.Project,
            projectId: selectedProjectId,
            version: selectedVersion,
        });

        setHiddenColumnIds(readHiddenColumnIds(storageKey));
    }, [
        activeTimeId,
        selectedEntityId,
        selectedProjectId,
        tenantId,
        selectedVersion,
        userInfo?.id,
    ]);

    const columnVisibilityKey = useMemo(
        () =>
            getGridColumnVisibilityKey({
                userId: userInfo?.id,
                tenantId,
                entityId: selectedEntityId,
                timeId: activeTimeId,
                dataSourceTypeId: DataSourceTypeId.Project,
                projectId: selectedProjectId,
                version: selectedVersion,
            }),
        [
            activeTimeId,
            selectedEntityId,
            selectedProjectId,
            tenantId,
            selectedVersion,
            userInfo?.id,
        ]
    );

    const analysisGridDataQuery = useGetDataGrid(
        {
            EntityId: selectedEntityId!,
            TimeId: activeTimeId!,
            DataSourceTypeId: DataSourceTypeId.Project,
            ProjectId: selectedProjectId,
            Version: selectedVersion,
            Skip: (analysisPage - 1) * analysisPageSize,
            Take: analysisPageSize,
            SearchTerm: analysisSearchValue || undefined,
            SortColumnId: sortColumnId,
            SortDescending: sortDescending,
            ColumnFilters:
                Object.keys(columnFilters).length > 0
                    ? JSON.stringify(columnFilters)
                    : undefined,
            IncludeAssignments: true,
            HiddenColumnIds: [...hiddenColumnIds].sort(
                (left, right) => left - right
            ),
        },
        { query: { enabled: isAnalysisScopeReady } }
    );
    const { data: analysisGridData, isLoading: isLoadingAnalysis } =
        analysisGridDataQuery;

    const { data: pools, isLoading: isLoadingPools } = useGetPools(
        {
            entityId: selectedEntityId,
            timeId: activeTimeId,
            visibleOnly: false,
        },
        {
            query: {
                enabled:
                    manualAssignmentModalVisible &&
                    !!selectedEntityId &&
                    !!activeTimeId,
                staleTime: 0,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            },
        }
    );
    const applyManualProjectPoolSplits = useApplyManualProjectPoolSplits();

    const [showResolvePerAsset, setShowResolvePerAsset] =
        useState<boolean>(false);
    const [showResolvePerRule, setShowResolvePerRule] =
        useState<boolean>(false);
    const [showKeywordAnalysis, setShowKeywordAnalysis] =
        useState<boolean>(false);
    const [showAddRule, setShowAddRule] = useState<boolean>(false);
    const [selectedKeyword, setSelectedKeyword] = useState<Keyword | null>(
        null
    );
    const [showDeleteProject, setShowDeleteProject] = useState<boolean>(false);
    const [projectToDelete, setProjectToDelete] =
        useState<ProjectListItemResponse | null>(null);
    const [showProjectUpload, setShowProjectUpload] = useState<boolean>(false);
    const [importProjectId, setImportProjectId] = useState<number>();
    const [isImportingData, setIsImportingData] = useState<boolean>(false);
    const [importUpload, setImportUpload] = useState<ImportUploadResponse>();
    const [isImportPreviewLoading, setIsImportPreviewLoading] =
        useState<boolean>(false);
    const [importPreviewError, setImportPreviewError] = useState<string>();
    const importUploadRequestId = useRef(0);
    const uploadImport = useUploadImportFile();
    const commitImport = useCommitProjectDataImport();
    const { data: fieldDefinitions } = useGetDataFieldDefinitions({
        dataSourceTypeId: DataSourceTypeId.Project,
    });

    // The previous import of this project, so its mapping can be offered for the new file.
    const importPreviousVersion = projects?.find(
        (project) => project.projectID === importProjectId
    )?.activeVersion;
    const { data: importPreviousColumns } = useGetDatasetColumns(
        {
            entityId: selectedEntityId!,
            timeId: activeTimeId!,
            dataSourceTypeId: DataSourceTypeId.Project,
            projectId: importProjectId,
            version: importPreviousVersion ?? undefined,
        },
        {
            query: {
                enabled:
                    showProjectUpload &&
                    !!selectedEntityId &&
                    !!activeTimeId &&
                    importProjectId !== undefined &&
                    !!importPreviousVersion,
            },
        }
    );

    const analysisContext: conflictResolveContext = {
        source: 'projectData',
        projectId: 'A10',
        version: '1.0',
        entityId: 'E001',
        timeId: 'T001',
    };

    const handleTabChange = (_index: number, value: string) => {
        setActiveTab(value);
    };

    const handleProjectSearch = (value: string) => {
        setProjectSearchValue(value);
    };

    const handleDownloadTemplate = () => {
        console.log('Download template');
    };

    const handleAddProject = () => {
        setShowAddProject(true);
    };

    const handleCreateProject = (data: {
        projectRef: string;
        projectName: string;
        projectDescription: string;
        contractor: string;
        contractDate: string;
    }) => {
        if (!selectedEntityId || !activeTimeId) return;
        createProjectMutate({
            data: {
                entityId: selectedEntityId,
                timeId: activeTimeId,
                projectRef: data.projectRef,
                projectName: data.projectName,
                projectDescription: data.projectDescription || null,
                contractor: data.contractor,
                contractDate: data.contractDate,
            },
        });
    };

    const handleOpenDeleteProject = (project: ProjectListItemResponse) => {
        setProjectToDelete(project);
        setShowDeleteProject(true);
    };

    const handleCloseDeleteProject = () => {
        setShowDeleteProject(false);
        setProjectToDelete(null);
    };

    const handleConfirmDeleteProject = () => {
        if (!projectToDelete?.projectID) return;
        deleteProjectMutation.mutate({
            params: {
                entityId: selectedEntityId,
                timeId: activeTimeId,
                projectId: projectToDelete.projectID,
                version: 0,
            },
        });
    };

    const handleOpenProjectUpload = (projectId?: number) => {
        if (projectId === undefined) return;
        setImportProjectId(projectId);
        setImportUpload(undefined);
        setImportPreviewError(undefined);
        setShowProjectUpload(true);
    };

    const handleImportFileSelected = (file: File): void => {
        const requestId = ++importUploadRequestId.current;

        if (!selectedEntityId || !activeTimeId) {
            setImportPreviewError(
                'Select an entity and time period before importing a file.'
            );
            return;
        }

        setImportUpload(undefined);
        setImportPreviewError(undefined);
        setIsImportPreviewLoading(true);
        void uploadImport
            .mutateAsync({
                data: {
                    EntityId: selectedEntityId,
                    TimeId: activeTimeId,
                    File: file,
                },
            })
            .then((response) => {
                if (requestId === importUploadRequestId.current) {
                    setImportUpload(response);
                }
            })
            .catch((error: unknown) => {
                if (requestId === importUploadRequestId.current) {
                    setImportPreviewError(getImportUploadErrorMessage(error));
                }
            })
            .finally(() => {
                if (requestId === importUploadRequestId.current) {
                    setIsImportPreviewLoading(false);
                }
            });
    };

    const handleImportDraft = async (draft: ImportDataDraft): Promise<void> => {
        if (
            !selectedEntityId ||
            !activeTimeId ||
            importProjectId === undefined
        ) {
            return;
        }

        const headers =
            importUpload?.worksheets?.[draft.worksheetIndex]?.headers ?? [];

        const fieldIdsByName = new Map(
            (fieldDefinitions ?? []).map((definition) => [
                definition.fieldName,
                definition.fieldId,
            ])
        );

        // The ordinal is the column's position in the file, which is what the import reads values by.
        const columns = [
            ...draft.baseMappings.map((mapping) => ({
                ordinal: headers.indexOf(mapping.sourceHeader),
                displayName: mapping.sourceHeader,
                dataType: 1,
                fieldId: fieldIdsByName.get(mapping.fieldName) ?? null,
                isVisible: true,
            })),
            ...draft.additionalHeaders.map((header) => ({
                ordinal: headers.indexOf(header),
                displayName: header,
                dataType: 1,
                fieldId: null,
                isVisible: true,
            })),
        ].filter((column) => column.ordinal >= 0);

        if (columns.length === 0) {
            setImportPreviewError(
                'The selected worksheet headers could not be matched to the mapping.'
            );
            return;
        }

        setImportPreviewError(undefined);
        setIsImportingData(true);

        try {
            const response = await commitImport.mutateAsync({
                data: {
                    uploadId: draft.uploadId,
                    entityId: selectedEntityId,
                    timeId: activeTimeId,
                    projectId: importProjectId,
                    worksheetIndex: draft.worksheetIndex,
                    isFinalImport: draft.isFinalImport,
                    columns,
                },
            });

            setShowProjectUpload(false);
            setImportUpload(undefined);
            showToast(
                'Success',
                `${response.rowCount ?? 0} rows were imported as version ${
                    response.version
                }.`,
                'success'
            );

            // FASORT always worked on the newest version, so the new import becomes the selected one.
            setSelectedProjectId(importProjectId);
            setSelectedVersion(response.version);
            setActiveProjectId(importProjectId);
            setActiveVersion(response.version);

            await Promise.all([
                queryClient.invalidateQueries({
                    queryKey: getGetProjectsForEntityQueryKey({
                        entityId: selectedEntityId,
                        timeId: activeTimeId,
                    }),
                }),
                queryClient.invalidateQueries({
                    queryKey: getGetProjectProgressBarStatusQueryKey(),
                }),
                queryClient.invalidateQueries({
                    queryKey: getGetDatasetColumnsQueryKey(),
                }),
                queryClient.invalidateQueries({
                    queryKey: getGetDataGridQueryKey(),
                }),
            ]);
        } catch (error) {
            setImportPreviewError(getImportUploadErrorMessage(error));
        } finally {
            setIsImportingData(false);
        }
    };

    const handleAnalysisSearch = (value: string) => {
        setAnalysisSearchValue(value);
        setAnalysisPage(1);
    };

    const handleManualAssignmentSuccess = () => {
        setManualAssignmentModalVisible(false);
        setAnalysisRowSelectionState({});
        showToast(
            'Success',
            'Manual categorisations have been applied.',
            'success'
        );
        void analysisGridDataQuery.refetch();
        void queryClient.invalidateQueries({
            queryKey: getGetProjectProgressBarStatusQueryKey({
                entityId: selectedEntityId,
                timeId: activeTimeId,
                projectId: selectedProjectId,
                version: selectedVersion,
            }),
        });
    };

    const handleSaveManualPoolSplits = (
        poolSplits: { poolId: number; splitPercent: number }[]
    ) => {
        if (
            !selectedEntityId ||
            !activeTimeId ||
            selectedProjectId === undefined ||
            selectedVersion === undefined ||
            selectedAnalysisRowIds.length === 0
        ) {
            return;
        }

        applyManualProjectPoolSplits.mutate(
            {
                data: {
                    entityId: selectedEntityId,
                    timeId: activeTimeId,
                    projectId: selectedProjectId,
                    version: selectedVersion,
                    rowIds: selectedAnalysisRowIds,
                    poolSplits,
                },
            },
            {
                onSuccess: handleManualAssignmentSuccess,
                onError: showGenericErrorToast,
            }
        );
    };

    const handleKeywordAnalysis = () => {
        setShowKeywordAnalysis(true);
    };

    const handleCreateRuleFromKeyword = (keyword: Keyword) => {
        setSelectedKeyword(keyword);
        setShowKeywordAnalysis(false);
        setShowAddRule(true);
    };

    const handleSaveRule = (values: SplitRuleValues) => {
        console.log('Rule created from project data:', values);
        setSelectedKeyword(null);
    };

    const handleCloseAddRule = () => {
        setShowAddRule(false);
        setSelectedKeyword(null);
    };

    const handleConflictResolveSelect = (value: string) => {
        if (value === 'perAsset') {
            setShowResolvePerAsset(true);
        } else if (value === 'perRule') {
            setShowResolvePerRule(true);
        }
    };

    const handleToggleColumns = useCallback(
        (value: string) => {
            const columnId = Number(value);
            const next = new Set(hiddenColumnIds);

            if (next.has(columnId)) {
                next.delete(columnId);
            } else {
                next.add(columnId);
            }

            setHiddenColumnIds(next);
            saveHiddenColumnIds(columnVisibilityKey, next);
        },
        [columnVisibilityKey, hiddenColumnIds]
    );

    const handleProjectFilter = (value: string) => {
        // Values are "projectId" for all projects, or "projectId:version" for one import.
        const [projectId, version] = value.split(':');
        const selectedProjectId = Number(projectId);
        const selectedVersion =
            version === undefined ? undefined : Number(version);

        setSelectedProjectId(selectedProjectId);
        setSelectedVersion(selectedVersion);
        setActiveProjectId(selectedProjectId);
        setActiveVersion(selectedVersion);
        setAnalysisPage(1);
    };

    const handleRunRules = () => {
        console.log('Run Rules clicked');
    };

    const handleResetRules = () => {
        console.log('Reset Rules clicked');
    };

    const handleDownloadReport = () => {
        console.log('Download Report clicked');
    };

    const handleResolvePerAsset = (
        context: conflictResolveContext,
        assetId: string,
        chosenRule: { ruleId: string; ruleShortName: string }
    ) => {
        console.log('Resolve per asset:', { context, assetId, chosenRule });
    };

    const handleResolvePerRule = (
        context: conflictResolveContext,
        chosenRule: { ruleId: string; ruleShortName: string },
        rejectedRule: { ruleId: string; ruleShortName: string },
        numberOfAssets: number
    ) => {
        console.log('Resolve per rule:', {
            context,
            chosenRule,
            rejectedRule,
            numberOfAssets,
        });
    };

    const projectColumns = useMemo<ColumnDef<ProjectListItemResponse, any>[]>(
        () => [
            {
                accessorFn: (row) => row.projectRef ?? String(row.projectID),
                id: 'projectId',
                header: 'Project ID',
                enableSorting: true,
            },
            {
                accessorFn: (row) => row.projectName ?? '',
                id: 'name',
                header: 'Name',
                enableSorting: true,
            },
            {
                accessorFn: (row) => row.projectDescription ?? '',
                id: 'description',
                header: 'Description',
                enableSorting: true,
                size: PROJECT_DESCRIPTION_COLUMN_WIDTH,
                maxSize: PROJECT_DESCRIPTION_COLUMN_WIDTH,
                cell: ({ getValue }) => {
                    const value = getValue() as string;
                    return <span title={value}>{value}</span>;
                },
            },
            {
                accessorFn: (row) => row.contractor ?? '',
                id: 'mainContractor',
                header: 'Main Contractor',
                enableSorting: true,
            },
            {
                accessorFn: (row) => row.contractDate ?? '',
                id: 'contractDate',
                header: 'Contract Date',
                enableSorting: true,
            },
            {
                accessorFn: (row) =>
                    row.contractValue != null
                        ? row.contractValue.toLocaleString()
                        : '',
                id: 'contractValue',
                header: 'Contract Value',
            },
            {
                accessorFn: (row) =>
                    row.finalValue != null
                        ? row.finalValue.toLocaleString()
                        : '',
                id: 'finalValue',
                header: 'Final Value',
            },
            {
                id: 'upload',
                header: '',
                enableSorting: false,
                size: 50,
                cell: ({ row }) =>
                    isReviewer ? null : (
                        <IconButton
                            onClick={() =>
                                handleOpenProjectUpload(row.original.projectID)
                            }
                            // FASORT stopped further imports once a final value was recorded.
                            disabled={(row.original.finalValue ?? 0) !== 0}
                            title={
                                (row.original.finalValue ?? 0) !== 0
                                    ? 'Final value imported'
                                    : 'Upload data'
                            }
                            testId={`import-project-${row.original.projectID}`}
                        >
                            <UploadIcon width={16} height={16} />
                        </IconButton>
                    ),
            },
            {
                id: 'delete',
                header: '',
                enableSorting: false,
                size: 50,
                cell: ({ row }) => (
                    <IconButton
                        onClick={() => handleOpenDeleteProject(row.original)}
                        icon="Appkit4-icon icon-delete-outline"
                        title="Delete project"
                    />
                ),
            },
        ],
        [isReviewer]
    );

    // Toggle menu lists the project columns the API actually returned for the current scope.
    const toggleColumnsItems = useMemo(() => {
        if (!analysisColumnsData) return [];
        return analysisColumnsData.map((c) => ({
            label: c.displayName ?? c.columnKey ?? '',
            value: String(c.columnId),
        }));
    }, [analysisColumnsData]);

    const projectFilterItems = useMemo(() => {
        const options: { label: string; value: string }[] = [];

        (projects ?? []).forEach((project) => {
            const name = project.projectName ?? `Project ${project.projectID}`;
            const versions = project.availableVersions ?? [];

            // A project with several imports gets one entry per version.
            if (versions.length === 0) {
                options.push({ label: name, value: String(project.projectID) });
                return;
            }

            versions.forEach((version) => {
                options.push({
                    label: `${name} (v${version})`,
                    value: `${project.projectID}:${version}`,
                });
            });
        });

        return options;
    }, [projects]);

    const selectedProjectTitle = useMemo(() => {
        const project = (projects ?? []).find(
            (item) => item.projectID === selectedProjectId
        );
        if (!project || selectedVersion === undefined) return null;

        const projectIdentifier = project.projectRef ?? project.projectID;
        const projectName =
            project.projectName ?? `Project ${projectIdentifier}`;
        return `[${projectIdentifier}: ${projectName} (v${selectedVersion})]`;
    }, [projects, selectedProjectId, selectedVersion]);

    const columnIdByKey = useMemo(() => {
        const map = new Map<string, number>();
        (analysisColumnsData ?? []).forEach((c) => {
            if (c.columnKey && c.columnId != null) {
                map.set(c.columnKey, c.columnId);
            }
        });

        return map;
    }, [analysisColumnsData]);

    const handleSortingChange = useCallback(
        (state: SortingState) => {
            const next = state[0];
            setSortColumnId(next ? columnIdByKey.get(next.id) : undefined);
            setSortDescending(next?.desc ?? false);
            setAnalysisPage(1);
        },
        [columnIdByKey]
    );

    const handleColumnFiltersChange = useCallback(
        (state: ColumnFiltersState) => {
            const next: Record<number, string> = {};
            state.forEach((f) => {
                const id = columnIdByKey.get(f.id);
                if (id != null && f.value != null && String(f.value) !== '') {
                    next[id] = String(f.value);
                }
            });
            setColumnFilters(next);
            setAnalysisPage(1);
        },
        [columnIdByKey]
    );

    const filteredAnalysisData = useMemo(() => {
        if (!analysisGridData?.rows) return [];

        return analysisGridData.rows.map((row) => ({
            ...row.values,
            _rowId: row.rowId,
            _categorisation: row.categorisation,
            _assignments: row.assignments ?? [],
        }));
    }, [analysisGridData]);

    const analysisColumns = useMemo<ColumnDef<any, any>[]>(() => {
        if (!analysisColumnsData) return [];

        const dataCols: (ColumnDef<any, any> & { id: string })[] =
            analysisColumnsData
                .filter((c) => !hiddenColumnIds.has(c.columnId!))
                .map((c) => {
                    const key = c.columnKey ?? '';
                    const col: ColumnDef<any, any> & { id: string } = {
                        accessorKey: key,
                        id: key,
                        header: c.displayName ?? key,
                        enableSorting: true,
                    };

                    if (key === 'Description' || key === 'PackageDescription') {
                        col.size = DESCRIPTION_COLUMN_MAX_WIDTH;
                        col.maxSize = DESCRIPTION_COLUMN_MAX_WIDTH;
                    }

                    if (c.dataType === 2) {
                        col.size = NUMERIC_COLUMN_WIDTH;
                        col.maxSize = NUMERIC_COLUMN_WIDTH;
                        col.cell = ({ getValue }) => {
                            const val = getValue() as number | null;
                            if (val == null) return '';
                            return (
                                <span
                                    className={
                                        val < 0
                                            ? 'cost-value cost-value--negative'
                                            : 'cost-value'
                                    }
                                >
                                    {val.toLocaleString('en-GB', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    })}
                                </span>
                            );
                        };
                    }

                    return col;
                });

        return dataCols;
    }, [analysisColumnsData, hiddenColumnIds]);

    return (
        <div className="project-data">
            <div className="project-data__header">
                <h1 className="project-data__title">
                    Project Data
                    {activeTab === 'analysis' && selectedProjectTitle && (
                        <span className="project-data__analysis-context">
                            {' '}
                            {selectedProjectTitle}
                        </span>
                    )}
                </h1>
            </div>

            <div className="project-data__tabs-row">
                <Tabs
                    tabs={tabs}
                    defaultActiveIndex={0}
                    onChange={handleTabChange}
                />

                {activeTab === 'projects' && (
                    <div className="project-data__header-actions">
                        <Button kind="text" onClick={handleDownloadTemplate}>
                            <span
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    color: '#000',
                                }}
                            >
                                <DownloadIcon width={16} height={16} />
                                Download Template
                            </span>
                        </Button>
                        <Button kind="primary" onClick={handleAddProject}>
                            Add Project
                        </Button>
                    </div>
                )}

                {activeTab === 'analysis' && (
                    <div className="project-data__header-actions">
                        <Button
                            kind="secondary"
                            icon="icon-refresh-outline"
                            onClick={handleResetRules}
                        >
                            Reset Rules
                        </Button>
                        <Button kind="primary" onClick={handleRunRules}>
                            Run Rules
                        </Button>
                    </div>
                )}
            </div>

            {activeTab === 'projects' && (
                <>
                    <div className="project-data__toolbar">
                        <div className="project-data__search">
                            <Search
                                placeholder="Search"
                                value={projectSearchValue}
                                onChange={(value) => handleProjectSearch(value)}
                                onClear={() => setProjectSearchValue('')}
                                noList
                            />
                        </div>
                    </div>

                    <div className="project-data__table">
                        <DataTable
                            data={projects ?? []}
                            columns={projectColumns}
                            className="project-data__projects-table"
                            sorting
                            columnFiltering
                            globalFilter={projectSearchValue}
                            rowSelection
                            getRowId={(row) => String(row.projectID)}
                        />
                    </div>

                    <Pagination />
                </>
            )}

            {activeTab === 'analysis' && (
                <>
                    <div className="project-data__toolbar">
                        <div className="project-data__search">
                            <Search
                                placeholder="Search"
                                value={analysisSearchValue}
                                onChange={(value) =>
                                    handleAnalysisSearch(value)
                                }
                                onClear={() => setAnalysisSearchValue('')}
                                noList
                            />
                        </div>

                        <div className="project-data__toolbar-actions">
                            <Button
                                kind="secondary"
                                onClick={handleKeywordAnalysis}
                            >
                                Keyword Analysis
                            </Button>

                            <MenuButton
                                label="Conflict Resolve"
                                kind="secondary"
                                items={conflictResolveOptions}
                                onSelect={handleConflictResolveSelect}
                            />

                            <MenuButton
                                label="Toggle Columns"
                                kind="secondary"
                                testId="project-data-toggle-columns"
                                keepOpenOnSelect
                                items={toggleColumnsItems.map((column) => ({
                                    ...column,
                                    testId: `project-data-column-${column.value}`,
                                    label: `${
                                        !hiddenColumnIds.has(
                                            Number(column.value)
                                        )
                                            ? '✓ '
                                            : ''
                                    }${column.label}`,
                                }))}
                                onSelect={handleToggleColumns}
                            />

                            <MenuButton
                                label="Projects"
                                kind="secondary"
                                items={projectFilterItems}
                                onSelect={handleProjectFilter}
                            />

                            <Button
                                kind="secondary"
                                onClick={handleDownloadReport}
                            >
                                Download Report
                            </Button>
                        </div>
                    </div>

                    {selectedAnalysisRowIds.length > 0 && (
                        <div className="project-data__selection-bar">
                            <span>
                                {selectedAnalysisRowIds.length}{' '}
                                {selectedAnalysisRowIds.length === 1
                                    ? 'Project'
                                    : 'Projects'}{' '}
                                Selected
                            </span>
                            <div>
                                <Button
                                    kind="secondary"
                                    onClick={() => {
                                        setAnalysisRowSelectionState({});
                                    }}
                                >
                                    Clear Selection
                                </Button>
                                <Button
                                    kind="secondary"
                                    onClick={() =>
                                        setManualAssignmentModalVisible(true)
                                    }
                                >
                                    Manually categorize
                                </Button>
                            </div>
                        </div>
                    )}

                    <div className="project-data__table">
                        {isLoadingAnalysis && isAnalysisScopeReady ? (
                            <p>Loading...</p>
                        ) : (
                            <DataTable
                                data={filteredAnalysisData}
                                columns={analysisColumns}
                                sorting
                                manualSorting
                                onSortingChange={handleSortingChange}
                                columnFiltering
                                manualFiltering
                                onColumnFiltersChange={
                                    handleColumnFiltersChange
                                }
                                manualPagination
                                pageSize={analysisPageSize}
                                totalRows={analysisGridData?.filteredRows ?? 0}
                                rowSelection
                                getRowId={(row) => String(row._rowId)}
                                rowSelectionState={analysisRowSelectionState}
                                onRowSelectionStateChange={
                                    setAnalysisRowSelectionState
                                }
                            />
                        )}
                    </div>

                    <Pagination
                        currentPage={analysisPage}
                        totalItems={analysisGridData?.filteredRows ?? 0}
                        pageSize={analysisPageSize}
                        onPageChange={setAnalysisPage}
                        onPageSizeChange={(size) => {
                            setAnalysisPageSize(size);
                            setAnalysisPage(1);
                        }}
                    />
                </>
            )}

            {showResolvePerAsset && (
                <ResolvePerAssetModal
                    visible={showResolvePerAsset}
                    onClose={() => setShowResolvePerAsset(false)}
                    context={analysisContext}
                    onResolve={handleResolvePerAsset}
                />
            )}

            {showResolvePerRule && (
                <ResolvePerRuleModal
                    visible={showResolvePerRule}
                    onClose={() => setShowResolvePerRule(false)}
                    context={analysisContext}
                    onResolve={handleResolvePerRule}
                />
            )}

            {showKeywordAnalysis && (
                <KeywordAnalysisModal
                    visible={showKeywordAnalysis}
                    onClose={() => setShowKeywordAnalysis(false)}
                    onCreateRule={handleCreateRuleFromKeyword}
                    onResolve={() => {
                        setShowKeywordAnalysis(false);
                        setShowResolvePerAsset(true);
                    }}
                />
            )}

            {showAddRule && (
                <AddRuleModal
                    visible={showAddRule}
                    onClose={handleCloseAddRule}
                    onSave={handleSaveRule}
                    selectedKeyword={selectedKeyword}
                />
            )}

            {showDeleteProject && (
                <Modal
                    visible={showDeleteProject}
                    title="Delete Project?"
                    onClose={handleCloseDeleteProject}
                    modalStyle={{ width: '500px' }}
                >
                    <div>
                        <p>
                            Are you sure you want to delete project{' '}
                            <strong>
                                {projectToDelete?.projectName ??
                                    projectToDelete?.projectRef}
                            </strong>
                            ? This action is permanent.
                        </p>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '8px',
                                marginTop: '16px',
                            }}
                        >
                            <Button
                                kind="secondary"
                                onClick={handleCloseDeleteProject}
                            >
                                Cancel
                            </Button>
                            <Button
                                kind="primary"
                                onClick={handleConfirmDeleteProject}
                                disabled={deleteProjectMutation.isPending}
                            >
                                {deleteProjectMutation.isPending
                                    ? 'Deleting...'
                                    : 'Delete'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}

            {showProjectUpload && (
                <ImportDataModal
                    visible={showProjectUpload}
                    onClose={() => setShowProjectUpload(false)}
                    dataSourceTypeId={DataSourceTypeId.Project}
                    uploadId={importUpload?.uploadId}
                    worksheetPreviews={importUpload?.worksheets ?? undefined}
                    isPreviewLoading={isImportPreviewLoading}
                    isSubmitting={isImportingData}
                    previewError={importPreviewError}
                    entityId={selectedEntityId}
                    timeId={activeTimeId}
                    previousColumns={importPreviousColumns ?? undefined}
                    onFileSelected={handleImportFileSelected}
                    onSubmit={handleImportDraft}
                />
            )}

            {showAddProject && (
                <AddProjectModal
                    visible={showAddProject}
                    onClose={() => setShowAddProject(false)}
                    onSubmit={handleCreateProject}
                    isSubmitting={isCreating}
                />
            )}

            <ManualCatModal
                visible={manualAssignmentModalVisible}
                selectedRowCount={selectedAnalysisRowIds.length}
                onClose={() => setManualAssignmentModalVisible(false)}
                onSave={handleSaveManualPoolSplits}
                pools={pools ?? []}
                isLoadingPools={isLoadingPools}
                isSaving={applyManualProjectPoolSplits.isLoading}
            />
        </div>
    );
};

export default ProjectData;
